


import '../../../../assets/css/screens/customer/dashboard/subscription/SubscriptionHistory.css';
import AppNavbar from '../../../../components/customer/dashboard/apphome/appnavbar/AppNavbar';
import { useState, useEffect , useContext} from 'react';
import SubscriptionHistorySkeleton from "../../../../components/skeltonloading/SubscriptionHistorySkeleton";
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, Star, Crown, Check, Clock,
  AlertTriangle, XCircle, ChevronRight, RefreshCw,
  CalendarDays, Receipt, ShieldCheck,
} from 'lucide-react';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';



/* ── tier helpers ── */
const TIER_ICON  = { Basic: <Zap size={16} />, Pro: <Star size={16} />, Elite: <Crown size={16} /> };
const TIER_CLASS = { Basic: 'sh-card--basic', Pro: 'sh-card--pro', Elite: 'sh-card--elite' };
const TIER_COLOR = { Basic: '#3b82f6', Pro: '#7c3aed', Elite: '#d97706' };

/* ── live countdown hook ── */
function useCountdown(endDateStr) {
  const calc = () => {
    if (!endDateStr) return null;
    const diff = new Date(endDateStr) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000)  / 60000);
    const s = Math.floor((diff % 60000)    / 1000);
    const urgent = d === 0 && h < 24;
    return { days: d, hours: h, minutes: m, seconds: s, expired: false, urgent };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    if (!endDateStr) return;
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [endDateStr]);
  return time;
}

/* ── CountdownBlock ── */
function CountdownBlock({ endDate }) {
  const t = useCountdown(endDate);
  if (!t) return null;
  if (t.expired) return (
    <div className="sh-timer sh-timer--expired">
      <XCircle size={14} /> Plan expired
    </div>
  );
  return (
    <div className={`sh-timer${t.urgent ? ' sh-timer--urgent' : ''}`}>
      {t.urgent && <AlertTriangle size={13} />}
      <div className="sh-timer-units">
        {[['d', t.days], ['h', t.hours], ['m', t.minutes], ['s', t.seconds]].map(([lbl, val]) => (
          <div className="sh-timer-unit" key={lbl}>
            <span className="sh-timer-num">{String(val).padStart(2, '0')}</span>
            <span className="sh-timer-lbl">{lbl}</span>
          </div>
        ))}
      </div>
      <span className="sh-timer-text">remaining</span>
    </div>
  );
}

/* ── StatusPill ── */
function StatusPill({ status }) {
  const map = {
    active:    { label: 'Active',    cls: 'sh-pill--active'    },
    expired:   { label: 'Expired',   cls: 'sh-pill--expired'   },
    cancelled: { label: 'Cancelled', cls: 'sh-pill--cancelled' },
    pending:   { label: 'Pending',   cls: 'sh-pill--pending'   },
  };
  const cfg = map[status?.toLowerCase()] || map.pending;
  return <span className={`sh-pill ${cfg.cls}`}>{cfg.label}</span>;
}

/* ── format date ── */
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
export default function CustomerSubscriptionHistory() {
  const navigate = useNavigate();

 

  const { baseUrl } = useContext(AppContext);
const [current, setCurrent] = useState(null);
const [history, setHistory] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);


// fetch subscription history 

const fetchHistory = async () => {

    setLoading(true);

    try {

        const response = await axios.get(
            `${baseUrl}/customer/subscription/history`,
            {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`
                }
            }
        );

        setCurrent(response.data.current_subscription);
        setHistory(response.data.history);

    } catch (error) {

        console.log(error);
        setError("Unable to load subscription history.");

    } 
    finally{
    setLoading(false);
};

};


useEffect(() => {
    fetchHistory();
}, []);

  return (
    <>
      <AppNavbar />
      <div className="sh-page">

        {/* ── HEADER ── */}
        <div className="sh-header">
          <button className="sh-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <div className="sh-header-text">
            <h3 className="sh-page-title">My Subscriptions</h3>
            <p className="sh-page-sub">Track your active plan and billing history</p>
          </div>
        <button
    className="sh-refresh"
    onClick={fetchHistory}
>
    <RefreshCw size={18}/>
</button>
        </div>

        {/* ── LOADING ── */}
       {loading && (
  <SubscriptionHistorySkeleton />
)}

        {/* ── ERROR ── */}
        {!loading && error && (
          <div className="sh-error">
            <AlertTriangle size={28} />
            <p>{error}</p>
            <button className="sh-retry-btn">Try again</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ════ CURRENT PLAN ════ */}
            {current ? (
              <section className="sh-section">
                <div className="sh-section-label">
                  <ShieldCheck size={15} />
                  Current Plan
                </div>

                <div className={`sh-current-card ${TIER_CLASS[current.tier] || 'sh-card--basic'}`}>
                  {/* top row */}
                  <div className="sh-current-top">
                    <div className="sh-current-icon" style={{ color: TIER_COLOR[current.tier] }}>
                      {TIER_ICON[current.tier] || <Star size={16} />}
                    </div>
                    <div className="sh-current-info">
                      <p className="sh-current-name">{current.planName}</p>
                      <p className="sh-current-meta">
                        {current.tier} · ${current.price} / {current.duration}
                      </p>
                    </div>
                    <StatusPill status={current.status} />
                  </div>

                  {/* countdown */}
                  <CountdownBlock endDate={current.end_date} />

                  {/* dates */}
                  <div className="sh-dates">
                    <div className="sh-date-item">
                      <CalendarDays size={13} />
                      <span>Started</span>
                      <strong>{fmtDate(current.start_date)}</strong>
                    </div>
                    <div className="sh-date-sep" />
                    <div className="sh-date-item">
                      <CalendarDays size={13} />
                      <span>Expires</span>
                      <strong>{fmtDate(current.end_date)}</strong>
                    </div>
                  </div>

                  {/* features */}
                  {current.features?.length > 0 && (
                    <ul className="sh-current-features">
                      {current.features.map((f, i) => (
                        <li key={i}>
                          <Check size={13} className="sh-check" />
                          <span>
                            <strong>{f.feature_name}</strong>
                            {f.feature_value ? `: ${f.feature_value}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* upgrade CTA */}
                  <button
                    className="sh-upgrade-btn"
                    onClick={() => navigate('/customer-subscription')}
                  >
                    Upgrade Plan <ChevronRight size={15} />
                  </button>
                </div>
              </section>
            ) : (
              <div className="sh-no-active">
                <div className="sh-no-active-icon">💎</div>
                <p className="sh-no-active-title">No active subscription</p>
                <p className="sh-no-active-sub">Choose a plan to unlock premium features.</p>
                <button className="sh-browse-btn" onClick={() => navigate(-1)}>
                  Browse Plans <ChevronRight size={15} />
                </button>
              </div>
            )}

            {/* ════ HISTORY ════ */}
            {history.length > 0 && (
              <section className="sh-section">
                <div className="sh-section-label">
                  <Receipt size={15} />
                  Billing History ({history.length})
                </div>

                <div className="sh-history-list">
                  {history.map((item, i) => (
                    <div key={item.id ?? i} className="sh-history-item">
                      <div
                        className="sh-history-accent"
                        style={{ background: TIER_COLOR[item.tier] || '#0491e1' }}
                      />
                      <div className="sh-history-icon" style={{ color: TIER_COLOR[item.tier] }}>
                        {TIER_ICON[item.tier] || <Star size={15} />}
                      </div>
                      <div className="sh-history-body">
                        <div className="sh-history-row1">
                          <p className="sh-history-name">{item.planName}</p>
                          <StatusPill status={item.status} />
                        </div>
                        <p className="sh-history-meta">
                          ${item.price} · {item.duration} · {item.tier}
                        </p>
                        <p className="sh-history-dates">
                          <Clock size={11} />
                          {fmtDate(item.start_date)} → {fmtDate(item.end_date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {history.length === 0 && !current && (
              <div className="sh-empty">
                <Receipt size={36} />
                <p>No subscription history yet.</p>
              </div>
            )}
          </>
        )}

        <div className="sh-footer">
          <ShieldCheck size={13} /> Secure payments · Cancel anytime
        </div>

      </div>
    </>
  );
}